/**
 * Abonnement au flux SSE `/events/stream` (docs/08 §12, docs/13 §5).
 *
 * Ce hook vivait dans `features/notifications` jusqu'à la phase 10. Il en est
 * sorti le jour où un second module a poussé dans le même flux : le canal
 * n'appartient pas aux notifications, il appartient à l'utilisateur. Le laisser
 * là-bas aurait obligé la messagerie à importer l'API d'une autre feature —
 * exactement ce que le déplacement du canal côté serveur a évité au backend.
 *
 * ⚠️ **`fetch` et non `EventSource`, et ce n'est pas un choix esthétique.**
 * `EventSource` ne sait pas poser d'en-tête `Authorization` ; la seule façon de
 * l'authentifier serait de mettre le jeton dans l'URL, où il finirait dans les
 * journaux du reverse proxy, dans l'historique du navigateur et dans le
 * `Referer` — trois endroits d'où un jeton d'accès ne se retire pas. On perd la
 * reconnexion automatique d'`EventSource`, qu'on réimplémente ici avec un
 * backoff, et c'est un bon échange.
 *
 * ⚠️ **Le flux ne porte pas la donnée, il porte un signal.** À réception, on
 * invalide les requêtes ; la liste est rechargée par la route, qui est la source
 * de vérité. Écrire le contenu du message directement dans le cache
 * introduirait un second chemin d'écriture — celui-là non paginé, non trié, et
 * sans les défauts que le serveur applique — et les deux divergeraient au
 * premier changement de schéma.
 */
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { API_BASE_URL } from "@/shared/api/config";
import { messagingKeys, notificationKeys } from "@/shared/api/query-keys";
import { useSessionStore } from "@/shared/auth/session";

/** Attente avant reconnexion, bornée : au-delà, on harcèlerait un serveur déjà en peine. */
const RECONNECT_MIN_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

export function useEventStream(): void {
  const queryClient = useQueryClient();
  const accessToken = useSessionStore((state) => state.accessToken);
  // Dans une ref, pas dans un état : sa mutation ne doit pas déclencher de rendu,
  // et la boucle de reconnexion doit lire la valeur courante et non celle
  // capturée à l'inscription.
  const attemptRef = useRef(0);

  useEffect(() => {
    if (accessToken === null) {
      return;
    }

    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const connect = async (): Promise<void> => {
      try {
        // L'unique `fetch` direct de l'application, et la seule exception à
        // docs/24 §3. `openapi-fetch` désérialise la réponse entière avant de la
        // rendre : c'est exactement ce qu'il ne faut pas faire d'un flux qui ne
        // se termine jamais — il attendrait une fin qui n'arrive pas, et aucune
        // notification ne parviendrait au client. Il faut le `ReadableStream`
        // brut, que seul `fetch` expose.
        //
        // Ce que l'exception coûte : le jeton et l'URL de base sont posés à la
        // main ici. Elle ne coûte pas le typage du contrat — il n'y a pas de
        // corps typé à valider, seulement des trames à relayer.
        // eslint-disable-next-line no-restricted-syntax -- flux SSE, voir ci-dessus
        const response = await fetch(`${API_BASE_URL}/api/v1/events/stream`, {
          headers: { Authorization: `Bearer ${accessToken}`, Accept: "text/event-stream" },
          signal: controller.signal,
        });
        if (!response.ok || response.body === null) {
          throw new Error(`stream refused: ${response.status}`);
        }

        attemptRef.current = 0;
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!stopped) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }
          buffer += decoder.decode(value, { stream: true });
          // SSE sépare les événements par une ligne vide. Le découpage doit se
          // faire sur le tampon accumulé : un chunk réseau n'a aucune raison de
          // s'aligner sur une frontière d'événement, et découper chaque chunk
          // isolément couperait un JSON en deux une fois sur dix.
          const frames = buffer.split("\n\n");
          buffer = frames.pop() ?? "";
          for (const frame of frames) {
            // Les trames commençant par « : » sont des commentaires — le
            // battement de maintien. Elles ne signalent rien et sont ignorées.
            if (frame.startsWith("data:")) {
              dispatch(queryClient, frame.slice("data:".length));
            }
          }
        }
      } catch {
        // Silencieux et volontairement : une coupure de flux est un incident
        // ordinaire (veille, changement de réseau, redéploiement). La remonter à
        // l'utilisateur ferait clignoter une erreur pour quelque chose qui se
        // répare tout seul, et dont il ne peut rien faire.
      }

      if (stopped || controller.signal.aborted) {
        return;
      }
      const delay = Math.min(RECONNECT_MIN_MS * 2 ** attemptRef.current, RECONNECT_MAX_MS);
      attemptRef.current += 1;
      timer = setTimeout(() => void connect(), delay);
    };

    void connect();

    return () => {
      stopped = true;
      controller.abort();
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [accessToken, queryClient]);
}

/**
 * Aiguillage d'une trame vers les caches qu'elle périme.
 *
 * ⚠️ **Un flux partagé ne s'invalide plus en bloc.** Tant que les notifications
 * étaient seules, invalider `notificationKeys.all` à chaque trame était juste et
 * suffisant. Depuis que la messagerie pousse dans le même canal, cette ligne
 * rechargerait le centre de notifications à chaque message reçu — et, ce qui est
 * pire, ne rechargerait *pas* la conversation ouverte. Le champ `event` de la
 * trame est donc lu, et une trame qu'on ne reconnaît pas n'invalide rien : une
 * version future du serveur peut en émettre que ce client ne connaît pas.
 */
function dispatch(queryClient: QueryClient, raw: string): void {
  let event: string;
  let conversationId: string | undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return;
    }
    const frame = parsed as { event?: unknown; conversation_id?: unknown };
    if (typeof frame.event !== "string") {
      return;
    }
    event = frame.event;
    conversationId = typeof frame.conversation_id === "string" ? frame.conversation_id : undefined;
  } catch {
    // Une trame illisible est un incident sans recours côté client : la source
    // de vérité reste la route, et le prochain chargement la servira.
    return;
  }

  if (event === "notification.created") {
    void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    return;
  }
  if (event === "message.created" || event === "message.updated" || event === "message.read") {
    // La liste **et** le badge, toujours : un message reçu change les deux, et
    // n'invalider que la conversation ouverte laisserait le compteur mentir.
    void queryClient.invalidateQueries({ queryKey: messagingKeys.all });
    if (conversationId !== undefined) {
      void queryClient.invalidateQueries({ queryKey: messagingKeys.messagesOf(conversationId) });
    }
  }
}
